import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SlRefresh } from "react-icons/sl";
import InputField from "components/fields/InputField";
import {
  KnowledgeSourceType,
  KnowledgeSourceFormFieldsName,
  MAX_KNOWLEDGE_SOURCE_BYTES,
  knowledgeSourceFormSchema,
  knowledgeSourceFormDefaultValues,
} from "constants/react-hook-form";

const SOURCE_TYPE_OPTIONS = [
  { value: KnowledgeSourceType.WEBSITE, label: "Website URL" },
  { value: KnowledgeSourceType.PDF, label: "PDF upload" },
  { value: KnowledgeSourceType.MANUAL, label: "Manual text" },
];

/**
 * Inline form for adding a single knowledge source. The chosen `source_type`
 * decides which reference field is shown; exactly one reference is validated
 * by knowledgeSourceFormSchema. On submit it delegates to the parent's
 * onSubmit(values) which performs the API call and returns true on success so
 * the form can reset.
 */
const AddKnowledgeSourceForm = ({ onSubmit, isSubmitting }) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(knowledgeSourceFormSchema),
    defaultValues: knowledgeSourceFormDefaultValues,
  });

  const sourceType = watch(KnowledgeSourceFormFieldsName.SOURCE_TYPE);
  const text = watch(KnowledgeSourceFormFieldsName.TEXT);

  const textBytes = useMemo(() => (text ? new TextEncoder().encode(text).length : 0), [text]);

  const submit = async (values) => {
    const ok = await onSubmit?.(values);
    // Parent returns true when the add succeeded so we can clear the form.
    if (ok) {
      reset(knowledgeSourceFormDefaultValues);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="mb-4 space-y-4 rounded-lg border border-gray-200 bg-gray-50/60 p-4"
    >
      <div>
        <label className="ml-1 text-sm font-bold text-gray-700">Source type</label>
        <Controller
          name={KnowledgeSourceFormFieldsName.SOURCE_TYPE}
          control={control}
          render={({ field }) => (
            <div className="mt-2 flex flex-wrap gap-2">
              {SOURCE_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => field.onChange(option.value)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    field.value === option.value
                      ? "border-blue-500 bg-blue-50 text-blue-600"
                      : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        />
      </div>

      {sourceType === KnowledgeSourceType.WEBSITE && (
        <InputField
          control={control}
          name={KnowledgeSourceFormFieldsName.URL}
          label="URL"
          type="text"
          placeholder="https://example.com/about"
        />
      )}

      {sourceType === KnowledgeSourceType.PDF && (
        <div>
          <label className="ml-1 text-sm font-bold text-gray-700">PDF file</label>
          <Controller
            name={KnowledgeSourceFormFieldsName.FILE}
            control={control}
            render={({ field }) => (
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => field.onChange(e.target.files?.[0] || null)}
                className="mt-2 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm"
              />
            )}
          />
          {errors[KnowledgeSourceFormFieldsName.FILE]?.message && (
            <p className="mt-1 text-sm text-red-600">
              {errors[KnowledgeSourceFormFieldsName.FILE].message}
            </p>
          )}
        </div>
      )}

      {sourceType === KnowledgeSourceType.MANUAL && (
        <div>
          <label className="ml-1 text-sm font-bold text-gray-700">Text</label>
          <Controller
            name={KnowledgeSourceFormFieldsName.TEXT}
            control={control}
            render={({ field }) => (
              <textarea
                {...field}
                value={field.value ?? ""}
                rows={6}
                placeholder="Paste the information you want the bot to know…"
                className="mt-2 w-full rounded-xl border border-gray-200 bg-white p-3 text-sm outline-none"
              />
            )}
          />
          <div className="mt-1 flex items-center justify-between">
            {errors[KnowledgeSourceFormFieldsName.TEXT]?.message ? (
              <p className="text-sm text-red-600">
                {errors[KnowledgeSourceFormFieldsName.TEXT].message}
              </p>
            ) : (
              <span />
            )}
            <span
              className={`text-xs ${
                textBytes > MAX_KNOWLEDGE_SOURCE_BYTES ? "text-red-500" : "text-gray-400"
              }`}
            >
              {(textBytes / 1000).toFixed(1)} / {Math.round(MAX_KNOWLEDGE_SOURCE_BYTES / 1000)} KB
            </span>
          </div>
        </div>
      )}

      <InputField
        control={control}
        name={KnowledgeSourceFormFieldsName.TITLE}
        label="Title (optional)"
        type="text"
        placeholder="A short label for this source"
      />

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600 disabled:opacity-60"
        >
          {isSubmitting ? (
            <>
              <SlRefresh className="animate-spin" />
              Adding…
            </>
          ) : (
            "Add source"
          )}
        </button>
      </div>
    </form>
  );
};

export default AddKnowledgeSourceForm;
